import { Calendar, Clock } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import {
  formatJobScheduleDateTime,
  formatJobScheduleDuration,
} from '@/lib/job-schedule-form'
import type { Job } from '@/types'

type JobScheduleSummaryCardProps = {
  job: Job
}

const DAY_MS = 24 * 60 * 60 * 1000

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

function parseScheduleDate(value?: string | null) {
  const trimmed = value?.trim()
  if (!trimmed) {
    return null
  }

  const parsed = new Date(trimmed)
  return Number.isFinite(parsed.getTime()) ? parsed : null
}

function formatRelativeDays(days: number) {
  const absoluteDays = Math.abs(days)
  return `${absoluteDays} day${absoluteDays === 1 ? '' : 's'}`
}

function getTimingMeta(start: Date | null, end: Date | null, job: Job) {
  const isCompleted = job.status === 'completed' || job.workflow_status_code === 'completed'
  const today = startOfLocalDay(new Date())
  const startDay = start ? startOfLocalDay(start) : null
  const endDay = end ? startOfLocalDay(end) : null

  if (isCompleted) {
    return {
      label: 'Completed',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    }
  }

  if (endDay && endDay.getTime() < today.getTime()) {
    const days = Math.max(1, Math.round((today.getTime() - endDay.getTime()) / DAY_MS))
    return {
      label: `${formatRelativeDays(days)} overdue`,
      className: 'border-rose-200 bg-rose-50 text-rose-700',
    }
  }

  if (!startDay) {
    return {
      label: 'Schedule not set',
      className: 'border-slate-200 bg-slate-50 text-slate-700',
    }
  }

  const diffDays = Math.round((startDay.getTime() - today.getTime()) / DAY_MS)
  if (diffDays < 0) {
    return {
      label: 'In progress window',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
    }
  }
  if (diffDays === 0) {
    return {
      label: 'Due today',
      className: 'border-blue-200 bg-blue-50 text-blue-700',
    }
  }
  if (diffDays === 1) {
    return {
      label: 'Starts tomorrow',
      className: 'border-sky-200 bg-sky-50 text-sky-700',
    }
  }

  return {
    label: `Starts in ${formatRelativeDays(diffDays)}`,
    className: 'border-sky-200 bg-sky-50 text-sky-700',
  }
}

export function JobScheduleSummaryCard({ job }: JobScheduleSummaryCardProps) {
  const startValue = job.start_date ?? job.scheduled_start
  const endValue = job.end_date ?? job.scheduled_end
  const startDate = parseScheduleDate(startValue)
  const endDate = parseScheduleDate(endValue)
  const startLabel = formatJobScheduleDateTime(startValue) ?? 'Not set'
  const endLabel = formatJobScheduleDateTime(endValue) ?? 'Not set'
  const durationLabel = formatJobScheduleDuration(
    startValue,
    endValue,
    job.scheduled_hours ?? job.estimated_hours,
  ) ?? 'Not set'
  const timingMeta = getTimingMeta(startDate, endDate, job)

  return (
    <Card className="border-border/80">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Calendar className="h-4 w-4 text-primary" />
              Schedule
            </p>
            <Badge variant="outline" className={cn('font-medium', timingMeta.className)}>
              {timingMeta.label}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span><span className="font-medium text-foreground">Start:</span> {startLabel}</span>
            <span><span className="font-medium text-foreground">End:</span> {endLabel}</span>
            <span><span className="font-medium text-foreground">Duration:</span> {durationLabel}</span>
          </div>
        </div>
        {job.actual_hours != null && Number(job.actual_hours) > 0 ? (
          <div className="flex shrink-0 items-center gap-2 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span><span className="font-medium text-foreground">{job.actual_hours}</span> actual hrs</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
